<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, onMounted, proxyRefs, ref, watch } from 'vue';
import PostSaleCasesAPI from 'dashboard/api/postSaleCases';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, loading, refresh, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'postSale'),
    screen: computed(() => 'edit'),
  })
);

const activeSection = ref('Etapas e prazos');
const activeAction = ref('');
const actionsOpen = ref(false);
const statusCatalog = ref([]);
const statusCatalogLoading = ref(false);

const navigation = Object.freeze([
  ['i-lucide-file-text', 'Dados gerais'],
  ['i-lucide-clock-3', 'Etapas e prazos'],
  ['i-lucide-users-round', 'Participantes'],
  ['i-lucide-file-check-2', 'Documentos'],
  ['i-lucide-bell', 'Notificações'],
]);

const statusCopy = Object.freeze({
  amarelos: 'Atenção',
  em_dia: 'Em dia',
  laranjas: 'A vencer',
  sem_prazo: 'Sem prazo',
  vermelhos: 'Atrasado',
});

const initials = name =>
  String(name || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toUpperCase();

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
};

const formatDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const source = computed(() => model.detail?.source || {});
const transaction = computed(() => source.value.transaction || {});
const responsible = computed(() => source.value.responsible || null);
const manager = computed(() => source.value.manager || null);
const realDeadlines = computed(() => source.value.deadlines || []);
const processCode = computed(
  () => transaction.value.reference || `#${model.detail?.id || '—'}`
);
const processStatus = computed(
  () => source.value.status_label || model.detail?.status || 'Sem status'
);
const deadlineTone = computed(() => {
  const bucket = String(source.value.deadline?.bucket || 'sem_prazo');
  if (bucket === 'vermelhos') return 'border-rose-300 text-rose-600';
  if (bucket === 'laranjas' || bucket === 'amarelos') {
    return 'border-amber-300 text-amber-600';
  }
  return 'border-violet-500 text-[#5922c8]';
});
const actionAvailable = action =>
  model.actionOptions.some(option => option.name === action);
const actionLabel = computed(
  () =>
    model.actionOptions.find(option => option.name === activeAction.value)
      ?.label || 'Selecionar ação'
);
const statusOptions = computed(() =>
  statusCatalog.value
    .filter(option => option.id && option.id !== source.value.status)
    .map(option => ({ label: option.label || option.id, value: option.id }))
);

const deadlineIcon = deadline => {
  const code = String(deadline.code || '').toLocaleLowerCase('pt-BR');
  if (code.includes('escrit')) return 'i-lucide-pen-line';
  if (code.includes('registro')) return 'i-lucide-file-check-2';
  if (code.includes('vistoria')) return 'i-lucide-search-check';
  if (code.includes('chave')) return 'i-lucide-key-round';
  if (code.includes('credito') || code.includes('financiamento')) {
    return 'i-lucide-landmark';
  }
  if (code.includes('certid')) return 'i-lucide-scroll-text';
  if (code.includes('document')) return 'i-lucide-file-text';
  return 'i-lucide-list-checks';
};

const deadlineColor = deadline => {
  const bucket = String(deadline.bucket || 'sem_prazo');
  if (bucket === 'vermelhos') return 'border-rose-100 bg-rose-50 text-rose-600';
  if (bucket === 'laranjas' || bucket === 'amarelos') {
    return 'border-amber-100 bg-amber-50 text-amber-600';
  }
  return 'border-violet-100 bg-violet-50 text-[#5922c8]';
};

const stageIconTone = stage => {
  if (String(stage.code || '').toLocaleLowerCase('pt-BR').includes('chave')) {
    return 'border-emerald-100 bg-emerald-50 text-emerald-600';
  }

  return deadlineColor(stage);
};

const deadlineStatus = deadline =>
  statusCopy[String(deadline.bucket || '')] || 'Em acompanhamento';

const statusPayloadSummary = computed(() => {
  const payload = source.value.status_payload || {};
  const values = Object.values(payload)
    .filter(value => ['string', 'number'].includes(typeof value))
    .map(value => String(value).trim())
    .filter(Boolean);

  return values[0] || '';
});

const conciseStageLabel = value =>
  String(value || 'Prazo do processo')
    .replace(/\s+p[uú]blica$/i, '')
    .replace(/\s+no\s+cart[oó]rio\s+de\s+im[oó]veis$/i, '')
    .replace(/\s+de\s+entrega$/i, '');

const deadlineDetails = deadline => {
  if (Number.isFinite(Number(deadline.days_remaining))) {
    const days = Number(deadline.days_remaining);
    return `${days} dia${days === 1 ? '' : 's'} para o prazo`;
  }

  return deadline.due_at ? `Prazo em ${formatDate(deadline.due_at)}` : '—';
};

const deadlineDescription = deadline => {
  const message = deadline.message || {};
  const description =
    message.description ||
    message.details ||
    message.summary ||
    deadline.description;

  return description || 'Prazo registrado no processo';
};

const priorityCodes = Object.freeze([
  'checklist:escritura',
  'checklist:registro',
  'checklist:vistoria',
  'checklist:chaves',
]);

const stageSource = computed(() => {
  const deadlinesByCode = new Map(
    realDeadlines.value.map(deadline => [deadline.code, deadline])
  );
  const processMilestones = priorityCodes
    .map(code => deadlinesByCode.get(code))
    .filter(Boolean);

  const deadlines = (
    processMilestones.length ? processMilestones : realDeadlines.value
  )
    .slice(0, 4)
    .map(deadline => ({
      action: deadline.action || null,
      bucket: deadline.bucket || 'sem_prazo',
      code: deadline.code || deadline.message?.status || 'prazo',
      description: deadlineDescription(deadline),
      details: deadlineDetails(deadline),
      dueAt: deadline.due_at,
      icon: deadlineIcon(deadline),
      label: conciseStageLabel(deadline.message?.status),
    }));

  const statusStage = statusPayloadSummary.value
    ? [
        {
          bucket: source.value.deadline?.bucket || 'sem_prazo',
          code: `status:${source.value.status || 'unavailable'}`,
          current: true,
          description: 'Status atual do processo',
          details: 'Status atual',
          dueAt: source.value.status_due_at,
          icon: 'i-lucide-route',
          label: processStatus.value,
          observation: statusPayloadSummary.value,
        },
      ]
    : [];

  return [...statusStage, ...deadlines].slice(0, 4);
});

const stageSlots = computed(() => [
  ...stageSource.value,
  ...Array.from(
    { length: Math.max(0, 4 - stageSource.value.length) },
    (_, index) => ({
      bucket: 'sem_prazo',
      code: `unavailable-stage-${index}`,
      description: 'A API não retornou uma etapa adicional.',
      details: 'Nenhum prazo adicional foi retornado.',
      dueAt: '',
      icon: 'i-lucide-circle-dashed',
      label: 'Sem etapa disponível',
      unavailable: true,
    })
  ),
]);

const deadlineRows = computed(() =>
  stageSource.value.filter(stage => !stage.current).slice(0, 3)
);
const peopleRows = computed(() =>
  [
    { label: 'Responsável', person: responsible.value },
    { label: 'Gestor', person: manager.value },
  ].filter(row => row.person?.name)
);
const stageOwner = computed(() => {
  if (responsible.value?.name) {
    return { person: responsible.value, role: 'Responsável' };
  }

  if (manager.value?.name) return { person: manager.value, role: 'Gestor' };

  return null;
});
const missionDescription = computed(() => {
  const count = realDeadlines.value.length;
  if (!count) return 'Nenhum prazo retornado';
  return 'Prazos em acompanhamento';
});
const missionProgress = computed(() => {
  const total = realDeadlines.value.length;
  if (!total) return '—';
  const urgent = realDeadlines.value.filter(deadline =>
    ['vermelhos', 'laranjas', 'amarelos'].includes(deadline.bucket)
  ).length;
  return `${urgent}/${total}`;
});
const changeCount = computed(
  () => deadlineRows.value.length + peopleRows.value.length
);
const recognitionFeed = computed(() => {
  const events = Array.isArray(source.value.events) ? source.value.events : [];
  return events.slice(0, 3).map(event => ({
    actor: event.actor?.name || '—',
    date: event.occurred_at,
    id: event.id || event.sequence || event.occurred_at,
    label:
      event.reason ||
      event.to_status_label ||
      event.from_status_label ||
      event.event_type ||
      '—',
  }));
});
const canRunActiveAction = computed(() => {
  if (!activeAction.value || saving.value) return false;
  if (activeAction.value === 'transition') return Boolean(model.form.status);
  if (activeAction.value === 'assign')
    return Boolean(model.form.responsible_user_id);
  if (activeAction.value === 'addNote')
    return Boolean(model.form.reason?.trim());
  if (activeAction.value === 'cancel') {
    return Boolean(model.form.cancelled_at && model.form.reason?.trim());
  }
  return true;
});
const updateField = (key, event) => {
  model.form[key] = event?.target?.value ?? event;
};
const selectAction = action => {
  if (!actionAvailable(action)) return;
  activeAction.value = action;
  actionsOpen.value = true;
};
const runActiveAction = async () => {
  if (!canRunActiveAction.value) return;
  await model.runAction(activeAction.value);
};
const handlePrimaryAction = () => {
  if (!activeAction.value) {
    actionsOpen.value = true;
    activeAction.value = model.actionOptions[0]?.name || '';
    return;
  }
  runActiveAction();
};
const refreshProcess = () => refresh();
const loadStatusCatalog = async () => {
  statusCatalogLoading.value = true;
  try {
    const response = await PostSaleCasesAPI.statusCatalog();
    statusCatalog.value = Array.isArray(response?.data?.data)
      ? response.data.data
      : [];
  } catch {
    statusCatalog.value = [];
  } finally {
    statusCatalogLoading.value = false;
  }
};

onMounted(loadStatusCatalog);

watch(
  () => model.actionOptions.map(option => option.name).join(','),
  actionNames => {
    if (activeAction.value && actionNames.includes(activeAction.value)) return;
    activeAction.value = '';
  },
  { immediate: true }
);
</script>

<template>
  <main
    data-imudei-visual-id="post-sale-edit-root"
    class="mx-auto w-full max-w-[1325px] px-[19px] pb-28 pt-[13px] text-[#101958]"
  >
    <header
      data-imudei-visual-id="post-sale-edit-header"
      class="flex h-[61px] w-[972px] items-start justify-between"
    >
      <div>
        <p class="text-[10px] font-medium text-[#5b31bd]">
          Pós-venda <span class="mx-2 text-[#a5adc1]">/</span>
          {{ processCode }} <span class="mx-2 text-[#a5adc1]">/</span> Editar
        </p>
        <h1 class="mt-4 text-[28px] font-bold leading-none tracking-[-0.035em]">
          Editar processo
        </h1>
      </div>
      <button
        class="mt-4 flex items-start gap-2 text-left text-[9px] text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        type="button"
        aria-label="Atualizar processo"
        :disabled="loading || saving"
        @click="refreshProcess"
      >
        <i
          class="i-lucide-circle-check mt-0.5 size-4"
          :class="loading ? 'animate-spin' : ''"
        />
        <span>
          Dados sincronizados<br />
          <small class="text-[#68749a]">{{
            formatDateTime(source.updated_at)
          }}</small>
        </span>
      </button>
    </header>

    <section
      class="mt-[15px] grid grid-cols-[300px_655px_288px] items-start gap-x-[17px]"
    >
      <aside class="space-y-[14px]">
        <article
          data-imudei-visual-id="post-sale-edit-progress"
          class="flex h-[82px] items-center gap-3.5 rounded-2xl border border-[#e9e5f1] bg-white px-4 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
        >
          <span
            class="grid size-11 place-items-center rounded-full bg-violet-50 text-[#5922c8]"
          >
            <i class="i-lucide-target size-6" />
          </span>
          <span class="min-w-0 flex-1">
            <strong class="block text-[11px] text-[#5922c8]">Missão diária</strong>
            <small class="mt-1 block truncate text-[9px] text-[#68749a]">{{
              missionDescription
            }}</small>
          </span>
          <span
            class="grid size-10 place-items-center rounded-full border-[3px] bg-white text-center text-[7px] font-bold"
            :class="deadlineTone"
          >
            {{ missionProgress }}
          </span>
        </article>

        <article
          class="rounded-2xl border border-[#e9e5f1] bg-white p-3 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
        >
          <button
            v-for="item in navigation"
            :key="item[1]"
            class="flex h-[35px] w-full items-center gap-3 rounded-lg px-3 text-left text-[10px] transition-colors"
            :class="
              activeSection === item[1]
                ? 'bg-violet-50 font-semibold text-[#5922c8]'
                : 'text-[#5b678e]'
            "
            type="button"
            @click="activeSection = item[1]"
          >
            <i :class="item[0]" class="size-4" />{{ item[1] }}
          </button>
        </article>

        <article
          class="h-[102px] rounded-2xl border border-[#e9e5f1] bg-white p-4 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
        >
          <header class="flex items-center justify-between gap-2">
            <h2 class="text-[11px] font-medium">Stories do processo</h2>
            <span
              class="rounded-full bg-violet-50 px-2 py-1 text-[7px] font-semibold text-[#5922c8]"
              >Indisponível</span>
          </header>
          <div class="mt-3 flex items-center gap-2">
            <i
              v-for="item in 5"
              :key="item"
              class="relative grid size-10 place-items-center rounded-full border border-dashed border-[#cfc0ed] bg-gradient-to-br from-violet-50 via-white to-[#efe8fd] text-[#8564c9]"
              ><i class="i-lucide-image-off size-3" /><i
                class="absolute -bottom-px -right-px size-2 rounded-full border border-white bg-[#b89af2]"
            /></i>
            <i
              class="grid size-10 place-items-center rounded-full bg-violet-50 text-[#5922c8]"
              ><i class="i-lucide-plus size-4" /></i>
          </div>
        </article>

        <article
          class="h-[145px] rounded-2xl border border-[#e9e5f1] bg-white p-4 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
        >
          <header class="flex items-center justify-between gap-2">
            <h2 class="text-[11px] font-medium">Conquistas</h2>
            <span class="text-[8px] text-[#5922c8]">Indisponível</span>
          </header>
          <div class="mt-3 grid grid-cols-3 gap-2 text-center">
            <span
              v-for="item in [
                ['i-lucide-badge-check', 'bg-violet-50 text-[#8058dc]'],
                ['i-lucide-map', 'bg-emerald-50 text-emerald-600'],
                ['i-lucide-star', 'bg-amber-50 text-amber-600'],
              ]"
              :key="item[0]"
              ><i
                class="mx-auto grid size-14 place-items-center rounded-2xl border border-dashed border-[#d8d1e8]"
                :class="item[1]"
                ><i :class="item[0]" class="size-6" /></i><strong class="mt-2 block text-[8px]">—</strong><small class="mt-1 block text-[7px] text-[#8a94ae]">Sem dados</small></span>
          </div>
        </article>

        <article
          class="h-[156px] rounded-2xl border border-[#e9e5f1] bg-white p-4 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
        >
          <header class="flex items-center justify-between gap-2">
            <h2 class="text-[11px] font-medium">Ranking semanal</h2>
            <span class="text-[8px] text-[#5922c8]">Indisponível</span>
          </header>
          <div class="mt-3 space-y-2">
            <span
              v-for="position in 3"
              :key="position"
              class="flex h-7 items-center gap-2 rounded-lg border border-transparent px-2 text-[8px]"
              :class="
                position === 2
                  ? 'border-violet-100 bg-violet-50 text-[#5922c8]'
                  : 'text-[#69759c]'
              "
              ><b>{{ position }}</b><i
                class="grid size-5 place-items-center rounded-full border border-dashed border-[#d9d0e9] bg-[#faf9fd] text-[#a69abf]"
                ><i class="i-lucide-user-round size-3" /></i><small>—</small><b class="ml-auto">—</b></span>
          </div>
        </article>
      </aside>

      <section class="min-w-0 space-y-3">
        <article
          data-imudei-visual-id="post-sale-edit-steps"
          class="relative min-h-[725px] rounded-2xl border border-[#e9e5f1] bg-white pb-4 pt-[25px] shadow-[0_8px_24px_rgba(38,25,86,0.035)]"
        >
          <header class="flex items-start justify-between gap-3 px-5">
            <div>
              <h2 class="text-[17px] font-bold tracking-[-0.02em]">
                Etapas e prazos
              </h2>
              <p class="mt-4 text-[9px] text-[#68749a]">
                Configure as etapas do processo, responsáveis, prazos e
                dependências.
              </p>
            </div>
            <button
              class="flex h-[39px] w-[144px] items-center justify-center gap-2 rounded-lg border border-[#a982ff] text-[8px] font-semibold text-[#5922c8] disabled:cursor-not-allowed disabled:opacity-100"
              type="button"
              disabled
              title="A API não oferece inclusão de etapas"
            >
              <i class="i-lucide-plus size-4" />Adicionar etapa
            </button>
          </header>

          <div
            class="mt-[48px] mx-2 grid grid-cols-[138px_84px_98px_78px_80px_minmax(0,1fr)_16px] gap-1.5 px-1 text-[8px] font-medium text-[#53628d]"
          >
            <span class="pl-6">Etapa</span><span>Status</span><span>Responsável</span><span>Prazo alvo</span><span>Depende de</span><span>Observações</span><span />
          </div>

          <div class="mt-5 mx-2 space-y-6">
            <article
              v-for="stage in stageSlots"
              :key="stage.code"
              class="grid min-h-[102px] grid-cols-[138px_84px_98px_78px_80px_minmax(0,1fr)_16px] items-center gap-1.5 rounded-xl border border-[#eeeaf5] px-1 py-4 shadow-[0_2px_8px_rgba(43,24,104,0.015)]"
            >
              <span class="flex min-w-0 items-center gap-0.5">
                <i
                  class="i-lucide-grip-vertical size-4 shrink-0"
                  :class="
                    stage.unavailable ? 'text-[#d9d4e6]' : 'text-[#8c74cb]'
                  "
                />
                <i
                  class="grid size-8 shrink-0 place-items-center rounded-lg border"
                  :class="
                    stage.unavailable
                      ? 'border-dashed border-[#d9d0e9] bg-[#faf9fd] text-[#aaa0c3]'
                      : stageIconTone(stage)
                  "
                >
                  <i :class="stage.icon" class="size-4" />
                </i>
                <span class="min-w-0"><strong
                    class="block truncate text-[8px] leading-3"
                    :class="stage.unavailable ? 'text-[#8992aa]' : ''"
                    >{{ stage.label }}</strong><small
                    class="mt-1 block max-h-6 overflow-hidden text-[7px] leading-3 text-[#68749a]"
                    >{{ stage.description }}</small><small
                    v-if="!stage.unavailable"
                    class="mt-1 block truncate text-[7px] leading-3 text-[#8a94ae]"
                    >{{ stage.details }}</small><i
                    v-if="stage.unavailable"
                    class="mt-1.5 block h-1 w-12 rounded-full bg-[#f0edf7]"
                /></span>
              </span>

              <span
                class="flex min-h-9 items-center justify-between rounded-lg px-2 text-[8px]"
                :class="
                  stage.unavailable
                    ? 'border border-dashed border-[#e1dbea] bg-[#faf9fd] text-[#9aa1b2]'
                    : deadlineColor(stage)
                "
              >
                <span class="truncate">{{
                  stage.unavailable ? '—' : deadlineStatus(stage)
                }}</span>
                <i
                  v-if="!stage.unavailable"
                  class="i-lucide-chevron-down ml-1 size-3 shrink-0 opacity-50"
                />
              </span>

              <span
                class="flex min-h-9 min-w-0 items-center gap-1 rounded-lg border border-[#e2ddea] px-2 text-[8px]"
              >
                <i
                  class="grid size-5 shrink-0 place-items-center rounded-full"
                  :class="
                    stageOwner?.person?.name
                      ? 'bg-[#eee6ff] text-[#5922c8]'
                      : 'bg-[#f5f2fa] text-[#a69abf]'
                  "
                  >{{
                    stageOwner?.person?.name
                      ? initials(stageOwner.person.name)
                      : '—'
                  }}</i>
                <span class="min-w-0 truncate"><strong class="block truncate font-medium">{{
                    stageOwner?.person?.name || '—'
                  }}</strong><small
                    v-if="stageOwner?.role === 'Gestor'"
                    class="block truncate text-[6px] text-[#68749a]"
                    >Gestor</small></span>
              </span>

              <span
                class="flex min-h-9 items-center justify-between gap-1 rounded-lg border border-[#e2ddea] px-2 text-[8px]"
              >
                <span class="truncate">{{
                  stage.unavailable ? '—' : formatDate(stage.dueAt)
                }}</span>
                <i
                  class="i-lucide-calendar-days size-3 shrink-0 text-[#65729a]"
                />
              </span>

              <span
                class="flex min-h-9 items-center justify-between gap-1 rounded-lg border border-[#e2ddea] px-2 text-[8px] text-[#68749a]"
                title="Dependência não informada pela API"
                ><span>—</span><i class="i-lucide-chevron-down size-3 shrink-0 opacity-50" /></span>
              <span
                class="flex min-h-10 items-center rounded-lg border border-[#e2ddea] px-2 text-[8px] leading-3 text-[#526087]"
                >{{
                  stage.unavailable
                    ? '—'
                    : stage.current
                      ? stage.observation || '—'
                      : '—'
                }}</span>
              <button
                class="grid size-6 place-items-center rounded-md text-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
                disabled
                title="A API não oferece exclusão de etapas"
              >
                <i class="i-lucide-trash-2 size-3" />
              </button>
            </article>
          </div>

          <p
            class="absolute bottom-6 left-2 right-2 flex items-center gap-2 rounded-lg border border-[#cbb7ff] bg-violet-50 px-3 py-3 text-[8px] text-[#5922c8]"
          >
            <i class="i-lucide-info size-4 shrink-0" />A sequência é definida
            pelo backend. Alterações de status permanecem disponíveis nas ações
            do processo.
          </p>
        </article>

        <article
          v-if="model.localError || error || model.notice"
          class="rounded-xl border px-4 py-3 text-[9px]"
          :class="
            model.localError || error
              ? 'border-rose-100 bg-rose-50 text-rose-700'
              : 'border-emerald-100 bg-emerald-50 text-emerald-700'
          "
        >
          {{ model.localError || error || model.notice }}
        </article>
      </section>

      <aside
        data-imudei-visual-id="post-sale-edit-sidebar"
        class="ml-1.5 flex h-[738px] flex-col gap-[14px]"
      >
        <article
          class="h-[466px] rounded-2xl border border-[#e9e5f1] bg-white p-5 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
        >
          <h2 class="text-[12px] font-bold">Resumo das alterações</h2>
          <h3 class="mt-5 flex items-center gap-2 text-[10px] font-medium">
            <i class="i-lucide-calendar-clock size-4 text-[#65729a]" />Prazos do
            processo
          </h3>
          <div v-if="deadlineRows.length" class="mt-3 space-y-3">
            <div
              v-for="row in deadlineRows"
              :key="row.code"
              class="min-h-[42px]"
            >
              <strong class="block truncate text-[8px]">{{
                  row.label
                }}</strong><span class="mt-2 flex items-center gap-2 text-[8px]"><small class="truncate text-[#68749a]">Prazo alvo</small><i
                    class="i-lucide-arrow-right size-3 text-[#c2c8d6]"
                  /><strong class="truncate text-emerald-600">{{
                    formatDate(row.dueAt)
                  }}</strong></span>
            </div>
          </div>
          <p v-else class="mt-3 text-[8px] text-[#68749a]">
            Nenhum prazo foi retornado para este processo.
          </p>

          <h3 class="mt-5 flex items-center gap-2 text-[10px] font-medium">
            <i
              class="i-lucide-users-round size-4 text-[#65729a]"
            />Participantes
          </h3>
          <div v-if="peopleRows.length" class="mt-3 space-y-3">
            <div
              v-for="row in peopleRows"
              :key="row.label"
              class="flex min-h-[42px] items-center gap-2"
            >
              <i
                class="grid size-6 shrink-0 place-items-center rounded-full bg-[#eee6ff] text-[7px] font-bold text-[#5922c8]"
                >{{ initials(row.person.name) }}</i>
              <span class="min-w-0 flex-1"><small class="block text-[7px] text-[#68749a]">{{
                  row.label
                }}</small><strong class="mt-0.5 block truncate text-[8px]">{{
                  row.person.name
                }}</strong></span>
            </div>
          </div>
          <p v-else class="mt-3 text-[8px] text-[#68749a]">
            Sem participantes atribuídos.
          </p>

          <button
            class="mt-5 flex w-full items-center justify-between border-t border-[#f0edf5] pt-3 text-left"
            type="button"
            @click="actionsOpen = true"
          >
            <span class="flex items-center gap-2 text-[9px] font-semibold"><i class="i-lucide-list-checks size-4 text-[#65729a]" />Ações do
              processo</span>
            <small class="text-[7px] text-[#5922c8]">{{ model.actionOptions.length }} disponível(is)</small>
          </button>
        </article>

        <article
          v-if="actionsOpen"
          data-imudei-visual-id="post-sale-edit-form"
          class="h-[260px] rounded-2xl border border-[#e9e5f1] bg-white p-5 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
        >
          <header class="flex items-center justify-between gap-2">
            <h2 class="text-[12px] font-bold">Ações do processo</h2>
            <button
              class="text-[8px] text-[#5922c8]"
              type="button"
              @click="actionsOpen = false"
            >
              Fechar
            </button>
          </header>
          <div class="mt-3 flex flex-wrap gap-1.5">
            <button
              v-for="action in model.actionOptions"
              :key="action.name"
              class="rounded-lg px-2 py-1.5 text-[7px] font-semibold"
              :class="
                activeAction === action.name
                  ? 'bg-violet-50 text-[#5922c8]'
                  : 'bg-[#faf9fd] text-[#65729a]'
              "
              type="button"
              @click="selectAction(action.name)"
            >
              {{ action.label }}
            </button>
          </div>
          <div
            v-if="activeAction"
            class="mt-3 rounded-lg border border-[#e8dffd] bg-[#fcfbff] p-2"
          >
            <strong class="block text-[8px] text-[#5922c8]">{{
              actionLabel
            }}</strong><label
              v-if="activeAction === 'transition'"
              class="mt-2 block text-[7px] font-semibold"
              >Novo status<select
                :value="model.form.status"
                class="mt-1 h-7 w-full rounded-md border border-[#e5e0ec] bg-white px-2 text-[7px] font-normal outline-none focus:border-[#805ce1]"
                :disabled="statusCatalogLoading"
                @change="updateField('status', $event)"
              >
                <option value="">
                  {{
                    statusCatalogLoading
                      ? 'Carregando catálogo...'
                      : 'Selecione'
                  }}
                </option>
                <option
                  v-for="option in statusOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select></label><label
              v-if="activeAction === 'transition'"
              class="mt-2 block text-[7px] font-semibold"
              >Prazo<input
                :value="model.form.status_due_at"
                class="mt-1 h-7 w-full rounded-md border border-[#e5e0ec] bg-white px-2 text-[7px] font-normal outline-none focus:border-[#805ce1]"
                type="datetime-local"
                @input="updateField('status_due_at', $event)"
/></label><label
              v-if="activeAction === 'assign'"
              class="mt-2 block text-[7px] font-semibold"
              >ID do responsável<input
                :value="model.form.responsible_user_id"
                class="mt-1 h-7 w-full rounded-md border border-[#e5e0ec] bg-white px-2 text-[7px] font-normal outline-none focus:border-[#805ce1]"
                min="1"
                type="number"
                @input="updateField('responsible_user_id', $event)"
/></label><label
              v-if="activeAction === 'cancel'"
              class="mt-2 block text-[7px] font-semibold"
              >Data<input
                :value="model.form.cancelled_at"
                class="mt-1 h-7 w-full rounded-md border border-rose-100 bg-white px-2 text-[7px] font-normal outline-none focus:border-rose-300"
                type="date"
                @input="updateField('cancelled_at', $event)"
/></label><label
              v-if="
                ['transition', 'assign', 'addNote', 'cancel'].includes(
                  activeAction
                )
              "
              class="mt-2 block text-[7px] font-semibold"
              >{{ activeAction === 'addNote' ? 'Nota' : 'Motivo'
              }}<textarea
                :value="model.form.reason"
                class="mt-1 min-h-[42px] w-full rounded-md border border-[#e5e0ec] bg-white px-2 py-1 text-[7px] font-normal outline-none focus:border-[#805ce1]"
                @input="updateField('reason', $event)"
              /></label><button
              class="mt-2 flex h-7 w-full items-center justify-center gap-1 rounded-md bg-gradient-to-r from-[#5c1ecf] to-[#7d19d9] px-2 text-[7px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
              type="button"
              :disabled="!canRunActiveAction || statusCatalogLoading"
              @click="runActiveAction"
            >
              <i class="i-lucide-check size-3" />{{
                saving ? 'Processando…' : actionLabel
              }}
            </button>
          </div>
          <p v-else class="mt-5 text-[8px] text-[#68749a]">
            Selecione uma ação permitida pelo backend.
          </p>
        </article>

        <article
          v-else
          data-imudei-visual-id="post-sale-edit-form"
          class="h-[260px] rounded-2xl border border-[#e9e5f1] bg-white p-5 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[12px] font-bold">Feed de reconhecimento</h2>
            <span class="text-[8px] text-[#5922c8]">{{
              recognitionFeed.length ? 'Atividade real' : 'Sem dados'
            }}</span>
          </header>
          <div v-if="recognitionFeed.length" class="mt-4 space-y-4">
            <div
              v-for="event in recognitionFeed"
              :key="event.id"
              class="flex gap-3"
            >
              <span
                class="grid size-8 shrink-0 place-items-center rounded-full bg-[#eee6ff] text-[8px] font-bold text-[#5922c8]"
                >{{ initials(event.actor) }}</span><span class="min-w-0 flex-1"><strong class="block text-[8px] leading-4">{{
                  event.label
                }}</strong><small class="mt-1 block truncate text-[7px] text-[#68749a]">{{
                  `${event.actor} · ${formatDateTime(event.date)}`
                }}</small></span>
            </div>
          </div>
          <div v-else class="mt-4 space-y-4">
            <div v-for="item in 3" :key="item" class="flex gap-3">
              <span
                class="grid size-8 shrink-0 place-items-center rounded-full border border-dashed border-[#d8d1e8] bg-[#faf9fd] text-[#9d8acb]"
                ><i class="i-lucide-sparkles size-4" /></span><span class="min-w-0 flex-1"><i class="mt-1 block h-2 w-28 rounded-full bg-[#f0edf7]" /><i
                  class="mt-2 block h-1.5 w-16 rounded-full bg-[#f5f2fa]"
              /></span>
            </div>
          </div>
        </article>
      </aside>
    </section>

    <footer
      data-imudei-visual-id="post-sale-edit-footer"
      class="fixed bottom-[47px] left-[232px] right-[20px] z-50 flex h-[52px] items-start bg-transparent"
    >
      <button
        class="h-10 w-[100px] rounded-lg border border-[#d9d2e8] text-[10px] font-medium"
        type="button"
        @click="emit('navigate', 'detail')"
      >
        Cancelar
      </button>
      <div
        class="absolute left-[317px] top-0 flex h-[52px] w-[354px] items-center rounded-lg border border-[#e8e4ef] bg-white px-5"
      >
        <span class="flex w-[165px] items-center gap-2 text-[9px] text-[#68749a]"><i
            class="i-lucide-git-compare-arrows size-4 text-[#5922c8]"
          />Comparar mudanças
          <b
            class="grid size-5 place-items-center rounded-full bg-violet-100 text-[8px] text-[#5922c8]"
            >{{ changeCount }}</b></span>
        <span class="flex items-center gap-2 border-l border-[#ece9f1] pl-5 text-[9px] text-emerald-700"><i class="i-lucide-shield-check size-5" />Integridade<br />Dados
          reais</span>
      </div>
      <div class="-mt-1.5 mr-5 ml-auto flex items-center gap-2.5">
        <button
          class="flex h-[37px] w-[162px] items-center justify-center gap-2 rounded-lg border border-[#8f64ee] text-[9px] font-semibold text-[#5922c8] disabled:opacity-100"
          type="button"
          disabled
          title="O backend não oferece rascunho para pós-venda"
        >
          <i class="i-lucide-save size-4" />Rascunho indisponível
        </button>
        <button
          class="flex h-[37px] w-[193px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#5c1ecf] to-[#7d19d9] text-[9px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          :disabled="
            !model.actionOptions.length ||
            saving ||
            Boolean(activeAction && !canRunActiveAction)
          "
          @click="handlePrimaryAction"
        >
          <i class="i-lucide-check size-4" />{{
            saving ? 'Processando…' : actionLabel
          }}
        </button>
      </div>
    </footer>
  </main>
</template>
